<template>
  <div class="space-y-4 p-4 max-w-2xl mx-auto">
    <div
      v-for="(item, index) in activities"
      :key="item.id"
    >
      <div
        v-if="index > 0 && itemDay(item) !== itemDay(activities[index - 1])"
        class="relative flex items-center my-6"
      >
        <div class="flex-grow border-t border-gray-200" />
        <span class="mx-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{{ formatDay(item) }}</span>
        <div class="flex-grow border-t border-gray-200" />
      </div>

      <!-- NEW LEAD -->
      <div
        v-if="item.type === 'NEW_LEAD'"
        class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
      >
        <div class="flex items-center gap-3 px-4 py-3 border-b border-blue-100 bg-blue-50">
          <img
            v-if="logoUrl(item.company)"
            :src="logoUrl(item.company)"
            class="h-9 w-auto flex-shrink-0 object-contain"
            style="max-width: 100px"
            alt=""
          >
          <div
            v-else
            class="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-blue-100 flex items-center justify-center"
          >
            <span class="text-blue-700 font-bold text-sm">{{ item.company.name[0] }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <a
              :href="`/companies/${item.company.id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-gray-900 hover:underline text-sm"
            >{{ item.company.name }}</a>
            <div class="text-xs text-gray-400">
              {{ formatDate(item.createdAt) }}
            </div>
          </div>
          <span
            v-if="item.company && item.company.productType"
            class="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ item.company.productType }}</span>
          <span
            v-if="item.lead && item.lead.origin"
            class="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ originLabel(item.lead.origin) }}</span>
          <span class="bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0">{{ $t('components.leadActivityFeed.newLead') }}</span>
        </div>
        <div
          v-if="item.lead"
          class="px-4 py-3"
        >
          <div class="font-semibold text-gray-800 text-sm">
            {{ fullName(item.lead) || item.lead.email }}
          </div>
          <div
            v-if="fullName(item.lead)"
            class="text-xs text-gray-500"
          >
            {{ item.lead.email }}
          </div>
        </div>
      </div>

      <!-- CONVERSION -->
      <div
        v-else-if="item.type === 'CONVERSION'"
        class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
      >
        <div class="flex items-center gap-3 px-4 py-3 border-b border-orange-100 bg-orange-50">
          <img
            v-if="logoUrl(item.company)"
            :src="logoUrl(item.company)"
            class="h-9 w-auto flex-shrink-0 object-contain"
            style="max-width: 100px"
            alt=""
          >
          <div
            v-else
            class="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-orange-100 flex items-center justify-center"
          >
            <span class="text-orange-700 font-bold text-sm">{{ item.company.name[0] }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <a
              :href="`/companies/${item.company.id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-gray-900 hover:underline text-sm"
            >{{ item.company.name }}</a>
            <div class="text-xs text-gray-400">
              {{ formatDate(item.createdAt) }}
            </div>
          </div>
          <span
            v-if="item.company && item.company.productType"
            class="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ item.company.productType }}</span>
          <span
            v-if="item.conversion"
            class="bg-orange-100 text-orange-700 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ conversionTypeLabel(item.conversion.type) }}</span>
          <span class="bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0">{{ $t('components.leadActivityFeed.conversion') }}</span>
        </div>
        <div
          v-if="item.conversion"
          class="px-4 py-3"
        >
          <div class="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">
            {{ conversionTypeLabel(item.conversion.type) }}
          </div>
          <div class="font-semibold text-gray-800 text-sm">
            {{ fullName(item.conversion) || item.conversion.email }}
          </div>
          <div
            v-if="fullName(item.conversion)"
            class="text-xs text-gray-500"
          >
            {{ item.conversion.email }}
          </div>
        </div>
      </div>
    </div>

    <!-- sentinel triggers next page load -->
    <div
      v-show="hasMore"
      ref="sentinel"
      class="h-4"
    />

    <div
      v-if="loadingMore"
      class="flex justify-center py-4"
    >
      <span class="text-gray-400 text-sm">{{ $t('common.loading') }}</span>
    </div>
  </div>
</template>

<script>
import GET_ALL_LEAD_ACTIVITIES from './getAllLeadActivities.gql'

export default {
  props: {
    filterPlatform: { type: String, default: null },
    filterCompanyId: { type: String, default: null },
    filterType: { type: String, default: null },
    pollIntervalMin: { type: Number, default: 5 }
  },
  data () {
    return {
      leadActivitiesPage: null,
      activities: [],
      currentPage: 1,
      total: 0,
      loadingMore: false,
      observer: null,
      lastFilterKey: null
    }
  },
  computed: {
    conversionTypeLabels () {
      return {
        RETURN_CALL: this.$t('components.leadActivityFeed.conversionTypes.RETURN_CALL'),
        DOWNLOAD: this.$t('components.leadActivityFeed.conversionTypes.DOWNLOAD'),
        IN_APP_VALUATION: this.$t('components.leadActivityFeed.conversionTypes.IN_APP_VALUATION'),
        VALUATION: this.$t('components.leadActivityFeed.conversionTypes.VALUATION'),
        PLOT_VALUATION: this.$t('components.leadActivityFeed.conversionTypes.PLOT_VALUATION'),
        PARTIAL_SALE: this.$t('components.leadActivityFeed.conversionTypes.PARTIAL_SALE'),
        PROPERTY_REQUEST: this.$t('components.leadActivityFeed.conversionTypes.PROPERTY_REQUEST'),
        LIFE_ANNUITY: this.$t('components.leadActivityFeed.conversionTypes.LIFE_ANNUITY')
      }
    },
    originLabels () {
      return {
        CONVERSION: this.$t('components.leadActivityFeed.originLabels.CONVERSION'),
        USER: this.$t('components.leadActivityFeed.originLabels.USER'),
        CRM: this.$t('components.leadActivityFeed.originLabels.CRM'),
        IMPORT: this.$t('components.leadActivityFeed.originLabels.IMPORT')
      }
    },
    hasMore () {
      return this.activities.length < this.total
    },
    queryInput () {
      const input = { page: 1, perPage: 10 }
      if (this.filterPlatform) input.platform = this.filterPlatform
      if (this.filterCompanyId) input.companyIds = [this.filterCompanyId]
      if (this.filterType) input.type = this.filterType
      return input
    }
  },
  watch: {
    pollIntervalMin (val) {
      this.$apollo.queries.leadActivitiesPage.startPolling(val * 60000)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && this.hasMore && !this.loadingMore && this.activities.length > 0) {
          this.loadMore()
        }
      })
      if (this.$refs.sentinel) {
        this.observer.observe(this.$refs.sentinel)
      }
    })
  },
  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    refresh () {
      this.$apollo.queries.leadActivitiesPage.refetch()
    },
    logoUrl (company) {
      return company?.images?.logo?.url || null
    },
    fullName (person) {
      return [person?.firstname, person?.lastname].filter(Boolean).join(' ')
    },
    conversionTypeLabel (type) {
      return this.conversionTypeLabels[type] || type
    },
    originLabel (origin) {
      return this.originLabels[origin] || origin
    },
    itemDay (item) {
      return new Date(item.createdAt).toDateString()
    },
    formatDay (item) {
      return new Date(item.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    },
    formatDate (dateStr) {
      return new Date(dateStr).toLocaleString()
    },
    async loadMore () {
      this.loadingMore = true
      try {
        const { data } = await this.$apollo.query({
          query: GET_ALL_LEAD_ACTIVITIES,
          variables: { input: { ...this.queryInput, page: this.currentPage + 1 } },
          fetchPolicy: 'no-cache'
        })
        if (data?.leadActivitiesPage) {
          this.activities = [...this.activities, ...data.leadActivitiesPage.activities]
          this.currentPage++
          this.total = data.leadActivitiesPage.total
        }
      } finally {
        this.loadingMore = false
      }
    }
  },
  apollo: {
    leadActivitiesPage: {
      query: GET_ALL_LEAD_ACTIVITIES,
      fetchPolicy: 'no-cache',
      pollInterval: 300000,
      variables () {
        return { input: this.queryInput }
      },
      result ({ data }) {
        if (!data?.leadActivitiesPage) return
        const currentKey = JSON.stringify(this.queryInput)
        if (currentKey !== this.lastFilterKey) {
          this.activities = data.leadActivitiesPage.activities
          this.currentPage = 1
          this.total = data.leadActivitiesPage.total
          this.lastFilterKey = currentKey
        } else {
          const existingIds = new Set(this.activities.map(a => a.id))
          const newItems = data.leadActivitiesPage.activities.filter(a => !existingIds.has(a.id))
          if (newItems.length > 0) {
            this.activities = [...newItems, ...this.activities]
            this.total = data.leadActivitiesPage.total
          }
        }
      }
    }
  }
}
</script>
